/**
 * DataGridCellRenderer
 *
 * Renders the appropriate editable cell variant based on the resolved cell type.
 * Extracted from AdvancedTableView to keep file sizes manageable.
 */

import type { CellVariant, CellSaveState, DataGridCellVariantProps } from './types';
import { DataGridCellText } from './DataGridCellText';
import { DataGridCellNumber } from './DataGridCellNumber';
import { DataGridCellSelect } from './DataGridCellSelect';
import { DataGridCellCheckbox } from './DataGridCellCheckbox';
import { DataGridCellDate } from './DataGridCellDate';
import { DataGridCellLongText } from './DataGridCellLongText';

interface DataGridCellRendererProps {
  variant: CellVariant;
  value: unknown;
  onCommit: (newValue: unknown) => void;
  onCancel: () => void;
  onStartEditing: () => void;
  saveState: CellSaveState;
  fieldname: string;
  options?: { label: string; value: string }[];
}

const VARIANT_MAP: Record<
  Exclude<CellVariant, 'readonly'>,
  React.ComponentType<DataGridCellVariantProps>
> = {
  text: DataGridCellText,
  number: DataGridCellNumber,
  select: DataGridCellSelect,
  checkbox: DataGridCellCheckbox,
  date: DataGridCellDate,
  longText: DataGridCellLongText,
};

export function DataGridCellRenderer({
  variant,
  value,
  onCommit,
  onCancel,
  onStartEditing,
  saveState,
  fieldname,
  options,
}: DataGridCellRendererProps) {
  if (variant === 'readonly') return null;

  const Component = VARIANT_MAP[variant];
  if (!Component) return null;

  return (
    <Component
      value={value}
      onCommit={onCommit}
      onCancel={onCancel}
      onStartEditing={onStartEditing}
      saveState={saveState}
      fieldname={fieldname}
      options={options}
    />
  );
}
